import { Config, Environment } from '../config';
import { RemoteValidateAction, ValidateAction, validateMocks } from './validation';
import { RegisterAction, registrationMocks, RemoteRegisterAction } from './registration';
import { ErrorResponse, RedirectResponse } from './response';
import { loginMocks, LoginRequest, RemoteLoginAction } from './login';
import { changePasswordMocks, PasswordChangeAction, RemotePasswordChangeAction } from './change-password';
import { changeEmailMocks, EmailChangeAction, RemoteEmailChangeAction } from './change-email';
import { playMocks, RemotePlayAction } from './play';
import { logoutMocks, RemoteLogoutAction } from './logout';
import { LoginStatusAction, loginStatusMocks, RemoteLoginStatusAction } from './login-status';
import {
	LoginHubGetPlayerEmailAction,
	LoginHubGetPlayerEmailErrorAction,
	loginHubGetPlayerEmailMocks,
	RemoteLoginHubGetPlayerEmailAction
} from './login-hub-get-player-email';
import {
	LoginHubResendConfirmationMailAction,
	LoginHubResendConfirmationMailErrorAction,
	loginHubResendConfirmationMailMocks,
	RemoteLoginHubResendConfirmationMailAction
} from './login-hub-resend-confirmation-mail';
import {
	RemoteTranslationAction,
	TransformationAction,
	TransformTranslationAction,
	transformTranslationMocks,
	TranslationAction,
	translationMocks,
} from './translation';
import {
	passwordForgottenResetMocks,
	PasswordForgottenVerifyAction,
	passwordForgottenVerifyMocks,
	RemotePasswordForgottenActions,
	ResetPasswordAction,
} from './password-forgotten';
import { RemoteSendCrossPlatformEmailAction, SendCrossPlatformAction, sendCrossPlatformMailMocks } from './send-cross-platform-email';
import { LoginCrossPlatformAction, loginCrossPlatformMailMocks, RemoteLoginCrossPlatformAction } from './login-cross-platform';
import { FacebookLoginAction, facebookLoginMocks, RemoteFacebookLoginAction } from './facebook-login';
import { FacebookConfirmAction, facebookConfirmMocks, RemoteFacebookConfirmAction } from './facebook-confirm';
import { FacebookLoginToGameAction, facebookLoginToGameMocks, RemoteFacebookLoginToGameAction } from './facebook-login-to-game';
import {
	OAuthConfirmAction,
	oauthConfirmMocks,
	OAuthLoginToGameAction,
	oauthLoginToGameMocks,
	OAuthStartAction,
	oauthStartMocks,
	RemoteOAuthConfirmAction,
	RemoteOAuthLoginToGameAction,
	RemoteOAuthStartAction,
} from './oauth';
import { isStringBlank } from '../utils';
import { loginHubConsumeMock, LoginHubConsumeTokenAction, RemoteConsumeTokenAction } from './login-hub-consume-token';
import { ChangeEmailAndPasswordAction, changeEmailAndPasswordMocks, RemoteChangeEmailAndPasswordAction } from './change-email-and-password';
import { RedeemChallengeAction } from './redeem-challenge';
import { challengeMocks, RemoteRedeemPowChallengeAction } from './redeem-challenge';
import { IssueChallengeAction } from './issue-challenge/reissue-challenge';
import { issueChallengeMocks, RemoteIssueChallengeAction } from './issue-challenge/reissue-challenge';

/**
 * Same site request policy to be used by all actions
 */
export const sameOriginRequestPolicy: RequestInit = {
	mode: 'same-origin',
	credentials: 'same-origin',
	referrerPolicy: 'same-origin'
};

export const addLPSLocaleHeader = (headers: HeadersInit, locale = 'en_DK'): HeadersInit => {
	return { ...headers, 'X-LPS-locale': isStringBlank(locale) ? 'en_DK' : locale };
};

/**
 * Redirect action sending a generic payload.
 */
export type RedirectAction<T> = (payload?: T) => Promise<RedirectResponse | ErrorResponse>;

export interface Actions {
	validate: ValidateAction;
	register: RegisterAction;
	login: RedirectAction<LoginRequest>;
	play: RedirectAction<undefined>;
	logout: RedirectAction<undefined>;
	loginStatus: LoginStatusAction;
	translation: TranslationAction;
	transformTranslation: TransformationAction;
	passwordForgottenVerify: PasswordForgottenVerifyAction;
	resetPassword: ResetPasswordAction;
	changePassword: PasswordChangeAction;
	changeEmail: EmailChangeAction;
	sendCrossPlatformEmail: SendCrossPlatformAction;
	loginCrossPlatform: LoginCrossPlatformAction;
	loginHubGetPlayerEmail: LoginHubGetPlayerEmailAction | LoginHubGetPlayerEmailErrorAction;
	loginHubResendConfirmationMail: LoginHubResendConfirmationMailAction | LoginHubResendConfirmationMailErrorAction;
	loginHubConsume: LoginHubConsumeTokenAction
	facebookLogin: FacebookLoginAction;
	facebookConfirm: FacebookConfirmAction;
	facebookLoginToGame: FacebookLoginToGameAction;
	oauthStart: OAuthStartAction;
	oauthConfirm: OAuthConfirmAction;
	oauthLoginToGame: OAuthLoginToGameAction;
	redeemChallenge: RedeemChallengeAction;
	issueChallenge: IssueChallengeAction;
	changeEmailAndPassword: ChangeEmailAndPasswordAction;
}

export const mockedActions = {
	validate: validateMocks,
	register: registrationMocks,
	login: loginMocks,
	play: playMocks,
	logout: logoutMocks,
	loginStatus: loginStatusMocks,
	translation: translationMocks,
	transformTranslation: transformTranslationMocks,
	passwordForgottenVerify: passwordForgottenVerifyMocks,
	resetPassword: passwordForgottenResetMocks,
	changePassword: changePasswordMocks,
	changeEmail: changeEmailMocks,
	sendCrossPlatformEmail: sendCrossPlatformMailMocks,
	loginCrossPlatform: loginCrossPlatformMailMocks,
	loginHubGetPlayerEmail: loginHubGetPlayerEmailMocks,
	loginHubResendConfirmationMail: loginHubResendConfirmationMailMocks,
	loginHubConsume: loginHubConsumeMock,
	facebookLogin: facebookLoginMocks,
	facebookConfirm: facebookConfirmMocks,
	facebookLoginToGame: facebookLoginToGameMocks,
	oauthStart: oauthStartMocks,
	oauthConfirm: oauthConfirmMocks,
	oauthLoginToGame: oauthLoginToGameMocks,
	changeEmailAndPassword: changeEmailAndPasswordMocks,
	challenge: challengeMocks,
	issueChallenge: issueChallengeMocks,
};

export class ActionsBuilder {
	build(config: Config, env: Environment = Environment.DEVELOPMENT): Actions {
		const defaultActions = {
			[Environment.DEVELOPMENT]: () => ({
				validate: mockedActions.validate.success,
				register: mockedActions.register.registerSuccess,
				login: mockedActions.login.successRedirect,
				play: mockedActions.play.successRedirect,
				logout: mockedActions.logout.successRedirect,
				loginStatus: mockedActions.loginStatus.successLoginStatus,
				translation: mockedActions.translation.successTranslation,
				transformTranslation: mockedActions.transformTranslation.successTransformation,
				passwordForgottenVerify: mockedActions.passwordForgottenVerify.successVerification,
				resetPassword: mockedActions.resetPassword.successfulReset,
				changePassword: mockedActions.changePassword.success,
				changeEmail: mockedActions.changeEmail.success,
				sendCrossPlatformEmail: mockedActions.sendCrossPlatformEmail.sendCrossPlatformMailSuccess,
				loginCrossPlatform: mockedActions.loginCrossPlatform.loginCrossPlatformMailSuccess,
				loginHubGetPlayerEmail: mockedActions.loginHubGetPlayerEmail.successLoginHubGetPlayerEmail,
				loginHubResendConfirmationMail: mockedActions.loginHubResendConfirmationMail.successLoginHubResendConfirmationMail,
				loginHubConsume: mockedActions.loginHubConsume.successConsume,
				facebookLogin: mockedActions.facebookLogin.playerNotFoundMock,
				facebookConfirm: mockedActions.facebookConfirm.success,
				facebookLoginToGame: mockedActions.facebookLoginToGame.success,
				oauthStart: mockedActions.oauthStart.playerNotFoundMock,
				oauthConfirm: mockedActions.oauthConfirm.success,
				oauthLoginToGame: mockedActions.oauthLoginToGame.success,
				changeEmailAndPassword: mockedActions.changeEmailAndPassword.success,
				redeemChallenge: mockedActions.challenge.success,
				issueChallenge: mockedActions.issueChallenge.success,
			}),
			[Environment.PRODUCTION]: () => ({
				validate: new RemoteValidateAction(config.getBaseUrl()).action,
				register: new RemoteRegisterAction(config.getBaseUrl()).action,
				login: new RemoteLoginAction(config.getBaseUrl()).action,
				play: new RemotePlayAction(config.getBaseUrl()).action,
				logout: new RemoteLogoutAction(config.getBaseUrl()).action,
				loginStatus: new RemoteLoginStatusAction(config.getBaseUrl()).action,
				translation: new RemoteTranslationAction(config.getBaseUrl()).action,
				transformTranslation: new TransformTranslationAction().action,
				passwordForgottenVerify: new RemotePasswordForgottenActions(config.getBaseUrl()).verifyAction,
				resetPassword: new RemotePasswordForgottenActions(config.getBaseUrl()).resetAction,
				changePassword: new RemotePasswordChangeAction(config.getBaseUrl()).action,
				changeEmail: new RemoteEmailChangeAction(config.getBaseUrl()).action,
				sendCrossPlatformEmail: new RemoteSendCrossPlatformEmailAction(config.getBaseUrl()).action,
				loginCrossPlatform: new RemoteLoginCrossPlatformAction(config.getBaseUrl()).action,
				loginHubGetPlayerEmail: new RemoteLoginHubGetPlayerEmailAction(config.getBaseUrl()).action,
				loginHubResendConfirmationMail: new RemoteLoginHubResendConfirmationMailAction(config.getBaseUrl()).action,
				loginHubConsume: new RemoteConsumeTokenAction(config.getBaseUrl()).action,
				facebookLogin: new RemoteFacebookLoginAction(config.getBaseUrl()).action,
				facebookConfirm: new RemoteFacebookConfirmAction(config.getBaseUrl()).action,
				facebookLoginToGame: new RemoteFacebookLoginToGameAction(config.getBaseUrl()).action,
				oauthStart: new RemoteOAuthStartAction(config.getBaseUrl()).action,
				oauthConfirm: new RemoteOAuthConfirmAction(config.getBaseUrl()).action,
				oauthLoginToGame: new RemoteOAuthLoginToGameAction(config.getBaseUrl()).action,
				changeEmailAndPassword: new RemoteChangeEmailAndPasswordAction(config.getBaseUrl()).action,
				redeemChallenge: new RemoteRedeemPowChallengeAction(config.getBaseUrl()).action,
				issueChallenge: new RemoteIssueChallengeAction(config.getBaseUrl()).action,
			})
		};

		return defaultActions[env]();
	}
}
